import React from 'react';
import { 
  HomeIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  HeartIcon,
  BeakerIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  QueueListIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../state/auth/useAuth';
import Logo from '../Logo';

const Sidebar = ({ activeTab, setActiveTab, onLogout }) => {
  const { user } = useAuth();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: HomeIcon,
      description: 'Overview and statistics'
    },
    {
      id: 'opd-admissions',
      label: 'OPD Admissions',
      icon: UserGroupIcon,
      description: 'Patient admissions management'
    },
    {
      id: 'patient-queue',
      label: 'Patient Queue',
      icon: QueueListIcon,
      description: 'Manage patient queues'
    },
    {
      id: 'patient-care',
      label: 'Patient Care',
      icon: ClipboardDocumentListIcon,
      description: 'Patient care and monitoring'
    },
    {
      id: 'vital-signs',
      label: 'Vital Signs',
      icon: HeartIcon,
      description: 'Record and monitor vitals'
    },
    {
      id: 'visits',
      label: 'Visits & Checkups',
      icon: BeakerIcon,
      description: 'Patient visits management'
    },
    {
      id: 'bed-management',
      label: 'Bed Management',
      icon: BuildingOffice2Icon,
      description: 'Hospital bed allocation'
    },
    {
      id: 'patient-notes',
      label: 'Patient Notes',
      icon: DocumentTextIcon,
      description: 'Medical notes and observations'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: ChartBarIcon,
      description: 'Generate and view reports'
    },
    {
      id: 'emergency',
      label: 'Emergency',
      icon: ExclamationTriangleIcon,
      description: 'Emergency protocols'
    }
  ];

  const bottomMenuItems = [
    {
      id: 'settings',
      label: 'Settings',
      icon: Cog6ToothIcon,
      description: 'System settings'
    }
  ];

  const MenuItem = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    
    return (
      <button
        onClick={onClick}
        className={`
          w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200
          ${isActive 
            ? 'bg-blue-600 text-white shadow-lg transform scale-105' 
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          }
        `}
        title={item.description}
      >
        <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-white' : 'text-gray-500'}`} />
        <div className="flex-1">
          <span className="text-sm font-medium">{item.label}</span>
        </div>
      </button>
    );
  };

  return (
    <div className="bg-white h-screen w-64 shadow-xl border-r border-gray-200 flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <Logo size="md" showText={true} />
        <p className="text-sm text-gray-500 mt-2">Nurse Portal</p>
      </div>

      {/* User Info */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'N'}
            </span>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">{user?.name || 'Nurse'}</p>
            <p className="text-xs text-gray-500">{user?.role || 'Nurse'}</p>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <nav className="space-y-2">
          {menuItems.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              isActive={activeTab === item.id}
              onClick={() => setActiveTab(item.id)}
            />
          ))}
        </nav>
      </div>

      {/* Bottom Navigation */}
      <div className="border-t border-gray-200 p-4 space-y-2">
        {bottomMenuItems.map((item) => (
          <MenuItem
            key={item.id}
            item={item}
            isActive={activeTab === item.id}
            onClick={() => setActiveTab(item.id)}
          />
        ))}
        
        {/* Logout Button */}
        <button
          onClick={onLogout}
          className="w-full flex items-center px-4 py-3 text-left rounded-lg transition-all duration-200 text-red-600 hover:bg-red-50"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
          <span className="text-sm font-medium">Logout</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;